import React, { useState } from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Download, Image } from "@mui/icons-material";
import { FileExcel } from 'react-bootstrap-icons';
import ExcelJS from "exceljs";

const BarChart3DCustom = ({ data, namekey, datakey, title, nameeje, nameejevertical, observaciones }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  // Preparar datos para Plotly 3D
  const chartData = data.map((item) => ({
    name: item[namekey],
    value: item[datakey],
  }));

  // Crear arrays para el gráfico 3D
  const xValues = chartData.map((item) => item.name);
  const yValues = chartData.map((_, index) => index);
  const zValues = chartData.map((item) => item.value);

  // Crear matriz Z para surface (opcional)
  const zMatrix = [zValues];

  const plotData = [
    {
      type: 'bar',
      x: xValues,
      y: zValues,
      marker: {
        color: zValues,
        colorscale: 'Viridis',
        showscale: true,
        colorbar: {
          title: nameejevertical || 'Valores',
          titleside: 'right'
        }
      },
      text: zValues.map(v => v.toLocaleString()),
      textposition: 'auto',
      hovertemplate: '<b>%{x}</b><br>' +
                     nameejevertical + ': %{y:,.0f}<br>' +
                     '<extra></extra>',
    }
  ];

  const layout = {
    title: {
      text: title || 'Gráfico de Barras 3D',
      font: { size: 18, color: '#344767' }
    },
    scene: {
      xaxis: { title: nameeje || 'Categorías' },
      yaxis: { title: '' },
      zaxis: { title: nameejevertical || 'Valores' },
      camera: {
        eye: { x: 1.5, y: 1.5, z: 1.3 }
      }
    },
    xaxis: {
      title: nameeje || 'Categorías',
      tickangle: -45,
      tickfont: {
        size: 10
      }
    },
    yaxis: {
      title: nameejevertical || 'Valores'
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    showlegend: false,
    margin: { l: 50, r: 50, t: 80, b: 100 },
    height: 500,
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: title || 'grafico_3d',
      height: 800,
      width: 1200,
      scale: 2
    }
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    // Encabezados
    const headers = [nameeje || 'Categoría', nameejevertical || 'Valor'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    // Datos
    chartData.forEach((item) => {
      worksheet.addRow([item.name, item.value]);
    });

    // Ajustar ancho de columnas
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </MDBox>

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default BarChart3DCustom;
